import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

// Order + color metadata. Translations come from i18n "segments.items".
const SEGMENT_META = {
  eigenaar: { color: "var(--forest)" },
  ruiter: { color: "var(--sand-dark)" },
  manege: { color: "var(--sand)" },
  trainer: { color: "var(--forest)" },
  professional: { color: "var(--sand-dark)" },
  training: { color: "var(--forest)" },
  stal: { color: "var(--ink)" },
  fokker: { color: "var(--sand-dark)" },
};

export const Segments = () => {
  const { t } = useTranslation();
  const items = t("segments.items", { returnObjects: true }) || [];
  const [active, setActive] = useState(items[0]?.key || "eigenaar");
  const current = items.find((s) => s.key === active) || items[0];
  if (!current) return null;
  const currentColor = (SEGMENT_META[current.key] || { color: "var(--forest)" }).color;

  return (
    <section
      id="segments"
      data-testid="segments-section"
      className="bg-background py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            {t("segments.eyebrow")}
          </p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
            {t("segments.title")}
          </h2>
          <p className="mt-6 text-[hsl(var(--ink-soft))]">{t("segments.intro")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-4">
            <div
              className="hide-scrollbar -mx-6 flex gap-2 overflow-x-auto px-6 lg:mx-0 lg:flex-col lg:gap-1.5 lg:overflow-visible lg:px-0"
              role="tablist"
            >
              {items.map((s) => {
                const isActive = s.key === active;
                const color = (SEGMENT_META[s.key] || { color: "var(--forest)" }).color;
                return (
                  <button
                    key={s.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`segment-tab-${s.key}`}
                    onClick={() => setActive(s.key)}
                    className={`group flex shrink-0 items-center justify-between gap-4 whitespace-nowrap rounded-2xl border px-5 py-4 text-left transition-all lg:whitespace-normal ${
                      isActive
                        ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-[hsl(var(--background))]"
                        : "border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full transition-all"
                        style={{
                          backgroundColor: isActive
                            ? "hsl(var(--sand))"
                            : `hsl(${color})`,
                        }}
                      />
                      <span className="text-sm font-medium">{s.label}</span>
                    </span>
                    <ArrowRight
                      className={`hidden h-4 w-4 transition-all lg:block ${
                        isActive
                          ? "translate-x-0 opacity-100"
                          : "-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-60"
                      }`}
                      strokeWidth={1.6}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-8">
            <article
              key={current.key}
              data-testid={`segment-panel-${current.key}`}
              className="animate-fade-up rounded-[2rem] border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-8 sm:p-12"
            >
              <p
                className="text-xs uppercase tracking-[0.22em]"
                style={{ color: `hsl(${currentColor})` }}
              >
                {current.label}
              </p>
              <h3 className="mt-4 font-display text-3xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-4xl lg:text-5xl">
                "{current.hook}"
              </h3>
              <p className="mt-6 max-w-xl text-[hsl(var(--ink-soft))]">
                {current.description}
              </p>

              <ul className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(current.bullets || []).map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 text-sm text-[hsl(var(--ink))]"
                  >
                    <span
                      className="mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full text-[10px] text-[hsl(var(--background))]"
                      style={{ backgroundColor: `hsl(${currentColor})` }}
                    >
                      ✓
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <a
                  href="#waitlist"
                  data-testid={`segment-cta-${current.key}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--ink))] px-6 py-3 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest))]"
                >
                  {t("segments.joinAs")} {current.label.toLowerCase()}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </a>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Segments;
